import { useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { LINK_FEED_QUEUE_KEYS, type LinkFeedQueueKey } from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useT } from '@/i18n/use-t';
import { useLocations } from '@/hooks/use-locations';
import {
  IP4_DEFAULT_PORT,
  IP4_DEFAULT_QUEUES,
  IP4_FOOTER_LINES_MAX,
  IP4_HEADER_LINES_MAX,
  IP4_LINE_MAX_LENGTH,
  IP4_PASSWORD_MIN_LENGTH,
  IP4_QUEUE_LABEL_KEY,
  IP4_QUEUE_STARTS_ON,
  useCreateIp4Source,
  useUpdateIp4Source,
  withIp4QueueDefaults,
  type Ip4Feed,
  type Ip4Protocol,
  type Ip4Queue,
  type Ip4Source,
  type Ip4SourceChange,
} from '@/hooks/use-ip4-sources';

/**
 * Add or edit an integration profile 4 ("Document feed (message queue)")
 * source (VL-353).
 *
 * The login entered here is stored encrypted and reaches only the store's
 * Link; the form never shows the password back (it says only that one is
 * set), so editing without typing a new one keeps the stored one.
 *
 * Two behaviours are spelled out on the form because an operator would
 * otherwise assume the opposite: switching the feed or a queue off PAUSES it
 * (what arrives meanwhile is sent when it is switched on again), and the
 * start number is the only way to skip documents.
 */

/** A queue row as typed; the start number stays text until it is checked. */
export type Ip4QueueForm = {
  enabled: boolean;
  exchange: string;
  routingKey: string;
  startFromNumber: string;
};

/** The form as typed. Lines are text, one line per row. */
export type Ip4Form = {
  name: string;
  protocol: Ip4Protocol;
  host: string;
  port: string;
  vhost: string;
  username: string;
  shopId: string;
  taxNumber: string;
  headerLines: string;
  footerLines: string;
  queues: Record<LinkFeedQueueKey, Ip4QueueForm>;
};

/** What the form holds once it has passed every check. */
export type Ip4FormValue = {
  name: string;
  connection: { protocol: Ip4Protocol; host: string; port: number; vhost: string };
  username: string;
  feed: Ip4Feed;
};

export type Ip4FormProblem =
  | 'host'
  | 'port'
  | 'shopId'
  | 'headerLines'
  | 'footerLines'
  | 'exchange'
  | 'startNumber';

const PROBLEM_MESSAGE_KEY: Record<Ip4FormProblem, string> = {
  host: 'settings.integrations.ip4.form.hostInvalid',
  port: 'settings.integrations.ip4.form.portInvalid',
  shopId: 'settings.integrations.ip4.form.shopIdInvalid',
  headerLines: 'settings.integrations.ip4.form.headerLinesInvalid',
  footerLines: 'settings.integrations.ip4.form.footerLinesInvalid',
  exchange: 'settings.integrations.ip4.form.exchangeRequired',
  startNumber: 'settings.integrations.ip4.form.startNumberInvalid',
};

/**
 * A host name or an address literal, as the server accepts it. Only the host
 * belongs in the field: a pasted address would put the scheme, a path or —
 * worst — a login into something that is shown back on the screen.
 */
const IP4_HOST = /^[A-Za-z0-9._:-]+$/;

/**
 * "broker.lan:5672" — the port pasted along with the host. The server's rule
 * lets it through (a colon belongs to an IPv6 literal, which has at least
 * two), and the Link would then look up a host by that whole name and fail.
 */
const HOST_WITH_PORT = /^[^:]+:\d*$/;

/**
 * Whether an edit would make the Link present the stored login to a different
 * broker (another host or port), or start sending it without encryption. The
 * server refuses that unless the password is typed again in the same edit.
 */
export function redirectsStoredLogin(
  source: Ip4Source,
  next: { protocol: Ip4Protocol; host: string; port: string },
): boolean {
  const before = source.connection;
  // As a number, the way it is sent: "05672" is still port 5672.
  if (next.host.trim() !== before.host || portFromText(next.port) !== before.port) return true;
  return before.protocol === 'amqps' && next.protocol !== 'amqps';
}

/**
 * The form a new source starts with: every queue on, with the default
 * routing — except the State Lottery queue (VL-375), whose routing the store
 * has not confirmed, which starts off.
 */
export function defaultIp4Queues(): Record<LinkFeedQueueKey, Ip4QueueForm> {
  const queues = {} as Record<LinkFeedQueueKey, Ip4QueueForm>;
  for (const key of LINK_FEED_QUEUE_KEYS) {
    queues[key] = {
      enabled: IP4_QUEUE_STARTS_ON[key],
      ...IP4_DEFAULT_QUEUES[key],
      startFromNumber: '',
    };
  }
  return queues;
}

function queueFormOf(queue: Ip4Queue): Ip4QueueForm {
  return {
    enabled: queue.enabled,
    exchange: queue.exchange,
    routingKey: queue.routingKey,
    startFromNumber: queue.startFromNumber === null ? '' : String(queue.startFromNumber),
  };
}

function formOf(source: Ip4Source | null): Ip4Form {
  if (source === null) {
    return {
      name: '',
      protocol: 'amqps',
      host: '',
      port: String(IP4_DEFAULT_PORT.amqps),
      vhost: '/',
      username: '',
      shopId: '',
      taxNumber: '',
      headerLines: '',
      footerLines: '',
      queues: defaultIp4Queues(),
    };
  }
  // A source whose document settings cannot be read back opens with them
  // empty, to be entered again — not with the defaults passed off as stored.
  const feed = source.feed;
  const queues = defaultIp4Queues();
  if (feed !== null) {
    for (const key of LINK_FEED_QUEUE_KEYS) queues[key] = queueFormOf(feed.queues[key]);
  }
  return {
    name: source.name,
    protocol: source.connection.protocol,
    host: source.connection.host,
    port: String(source.connection.port),
    vhost: source.connection.vhost,
    username: source.connection.credentials.username,
    shopId: feed?.shopId ?? '',
    taxNumber: feed?.taxNumber ?? '',
    headerLines: (feed?.headerLines ?? []).join('\n'),
    footerLines: (feed?.footerLines ?? []).join('\n'),
    queues,
  };
}

/**
 * One line per row. A blank row in the middle is a blank line on the receipt
 * and keeps its place; blank rows at the end are only stray Enter presses.
 * Spaces inside a line are kept — they may be centring it.
 */
export function linesFromText(text: string): string[] {
  const lines = text.split('\n').map((line) => line.replace(/\r$/, ''));
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

function linesFit(lines: string[], max: number): boolean {
  return lines.length <= max && lines.every((line) => line.length <= IP4_LINE_MAX_LENGTH);
}

/** A whole number from 1 to 65535; null when it is not one. */
export function portFromText(raw: string): number | null {
  if (!/^\d{1,5}$/.test(raw.trim())) return null;
  const port = Number(raw.trim());
  return port >= 1 && port <= 65_535 ? port : null;
}

/** Empty means "no start number"; otherwise a whole number, 0 or greater. */
export function startNumberFromText(raw: string): { ok: true; value: number | null } | null {
  const text = raw.trim();
  if (text === '') return { ok: true, value: null };
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? { ok: true, value } : null;
}

/** What saving a different start number does to documents not sent yet. */
export type Ip4StartNumberEffect = 'reachesBack' | 'skips' | 'cleared';

export type Ip4StartNumberChange = {
  queue: LinkFeedQueueKey;
  from: number | null;
  to: number | null;
  effect: Ip4StartNumberEffect;
};

const START_NUMBER_EFFECT_KEY: Record<Ip4StartNumberEffect, string> = {
  reachesBack: 'settings.integrations.ip4.form.startNumberEffect.reachesBack',
  skips: 'settings.integrations.ip4.form.startNumberEffect.skips',
  cleared: 'settings.integrations.ip4.form.startNumberEffect.cleared',
};

/**
 * The queues whose typed start number differs from the stored one (from
 * "none", for a new source), and what the Link does with each on its next
 * pass. Shown before saving because none of it can be taken back once
 * published, and because the two slips are easy: a field emptied by accident,
 * and 0 typed to mean "no start number" — which the Link reads as "send every
 * numbered document you hold", including those from before the feed existed.
 *
 * - a number where there was none, or a lower one: reaches back;
 * - a higher one: unsent documents below it are skipped;
 * - emptied: back to "dated from the first switch-on"; older unsent ones are
 *   skipped.
 *
 * A field that does not read as a number is left to the check on save.
 */
export function startNumberChanges(
  given: Ip4Source | null,
  queues: Record<LinkFeedQueueKey, Ip4QueueForm>,
): Ip4StartNumberChange[] {
  const source = given === null ? null : withIp4QueueDefaults(given);
  const changes: Ip4StartNumberChange[] = [];
  for (const key of LINK_FEED_QUEUE_KEYS) {
    const typed = startNumberFromText(queues[key].startFromNumber);
    if (typed === null) continue;
    const from = source?.feed?.queues[key].startFromNumber ?? null;
    const to = typed.value;
    if (to === from) continue;
    const effect: Ip4StartNumberEffect =
      to === null ? 'cleared' : from === null || to < from ? 'reachesBack' : 'skips';
    changes.push({ queue: key, from, to, effect });
  }
  return changes;
}

/**
 * The same bounds the server and the Link check, so a mistake is named here
 * rather than after a round trip. `queue` says which row a queue mistake is in.
 */
export function readIp4Form(
  form: Ip4Form,
):
  | { ok: true; value: Ip4FormValue }
  | { ok: false; problem: Ip4FormProblem; queue?: LinkFeedQueueKey } {
  const host = form.host.trim();
  if (host.length > 255 || !IP4_HOST.test(host) || HOST_WITH_PORT.test(host)) {
    return { ok: false, problem: 'host' };
  }
  const port = portFromText(form.port);
  if (port === null) return { ok: false, problem: 'port' };
  const shopId = form.shopId.trim();
  if (!/^[0-9]{1,9}$/.test(shopId)) return { ok: false, problem: 'shopId' };
  const headerLines = linesFromText(form.headerLines);
  if (!linesFit(headerLines, IP4_HEADER_LINES_MAX)) return { ok: false, problem: 'headerLines' };
  const footerLines = linesFromText(form.footerLines);
  if (!linesFit(footerLines, IP4_FOOTER_LINES_MAX)) return { ok: false, problem: 'footerLines' };

  const queues = {} as Ip4Feed['queues'];
  for (const key of LINK_FEED_QUEUE_KEYS) {
    const row = form.queues[key];
    const exchange = row.exchange.trim();
    // A switched-off queue keeps its routing: it is paused, not removed.
    if (exchange === '' || exchange.length > 255) {
      return { ok: false, problem: 'exchange', queue: key };
    }
    const start = startNumberFromText(row.startFromNumber);
    if (start === null) return { ok: false, problem: 'startNumber', queue: key };
    queues[key] = {
      enabled: row.enabled,
      exchange,
      routingKey: row.routingKey.trim(),
      startFromNumber: start.value,
    };
  }

  const taxNumber = form.taxNumber.trim();
  return {
    ok: true,
    value: {
      name: form.name.trim(),
      connection: { protocol: form.protocol, host, port, vhost: form.vhost.trim() },
      username: form.username.trim(),
      feed: {
        shopId,
        taxNumber: taxNumber === '' ? null : taxNumber,
        headerLines,
        footerLines,
        queues,
      },
    },
  };
}

/** The stored feed, key by key in the order `readIp4Form` builds one. */
function canonicalFeed(feed: Ip4Feed): Ip4Feed {
  const queues = {} as Ip4Feed['queues'];
  for (const key of LINK_FEED_QUEUE_KEYS) {
    const queue = feed.queues[key];
    queues[key] = {
      enabled: queue.enabled,
      exchange: queue.exchange,
      routingKey: queue.routingKey,
      startFromNumber: queue.startFromNumber,
    };
  }
  return {
    shopId: feed.shopId,
    taxNumber: feed.taxNumber,
    headerLines: feed.headerLines,
    footerLines: feed.footerLines,
    queues,
  };
}

/**
 * What an edit sends: a section only when something in it changed, so an
 * untouched section is never written back, plus the `updatedAt` the form was
 * loaded with — the server refuses the edit (409) if the source changed
 * meanwhile. A blank password is left out: it means "keep the stored one",
 * not "clear it".
 */
export function ip4Change(
  given: Ip4Source,
  next: Ip4FormValue & { enabled: boolean },
  password: string,
): Ip4SourceChange {
  const source = withIp4QueueDefaults(given);
  const change: Ip4SourceChange = { expectedUpdatedAt: source.updatedAt };
  if (next.name !== source.name) change.name = next.name;
  if (next.enabled !== source.enabled) change.enabled = next.enabled;
  const before = source.connection;
  const connectionChanged =
    next.connection.protocol !== before.protocol ||
    next.connection.host !== before.host ||
    next.connection.port !== before.port ||
    next.connection.vhost !== before.vhost ||
    next.username !== before.credentials.username ||
    password !== '';
  if (connectionChanged) {
    change.connection = {
      ...next.connection,
      // Not on the form: carried over so that sending the section whole does
      // not put them back to their defaults.
      ...(before.heartbeatSeconds === undefined
        ? {}
        : { heartbeatSeconds: before.heartbeatSeconds }),
      ...(before.connectTimeoutMs === undefined
        ? {}
        : { connectTimeoutMs: before.connectTimeoutMs }),
      credentials: { username: next.username, ...(password === '' ? {} : { password }) },
    };
  }
  if (
    source.feed === null ||
    JSON.stringify(next.feed) !== JSON.stringify(canonicalFeed(source.feed))
  ) {
    change.feed = next.feed;
  }
  return change;
}

/** How many refused fields are named; the rest would not fit a dialog. */
const SHOWN_ISSUES = 5;

/**
 * The server's refusal in its own words. A body that failed validation says
 * only "Validation failed" at the top and names the fields underneath, so
 * those are shown with it: the operator needs to know which field.
 */
export function serverMessage(cause: unknown, fallback: string): string {
  if (!isAxiosError(cause)) return fallback;
  const body = cause.response?.data as { message?: unknown; issues?: unknown } | undefined;
  if (typeof body?.message !== 'string' || body.message === '') return fallback;
  if (!Array.isArray(body.issues)) return body.message;
  const fields = body.issues
    .map((issue: unknown) => {
      const { path, message } = (issue ?? {}) as { path?: unknown; message?: unknown };
      if (typeof message !== 'string') return null;
      return typeof path === 'string' && path !== '' ? `${path}: ${message}` : message;
    })
    .filter((line): line is string => line !== null)
    .slice(0, SHOWN_ISSUES);
  return fields.length === 0 ? body.message : `${body.message} — ${fields.join('; ')}`;
}

export function Ip4SourceDialog({
  source: given,
  takenLocationIds = [],
  onClose,
}: {
  /** The source being edited; null to add one. */
  source: Ip4Source | null;
  /** Stores that already have a document feed: a store has one. */
  takenLocationIds?: readonly string[];
  onClose: () => void;
}): JSX.Element {
  // A source stored before VL-375 is read with its State Lottery queue as the Link runs it.
  const source = given === null ? null : withIp4QueueDefaults(given);
  const t = useT('admin_ui');
  const create = useCreateIp4Source();
  const update = useUpdateIp4Source();
  const locations = useLocations({ status: ['active'] });
  const creating = source === null;

  const [locationId, setLocationId] = useState('');
  const [form, setForm] = useState<Ip4Form>(() => formOf(source));
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  function set<K extends keyof Ip4Form>(key: K, value: Ip4Form[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function setProtocol(protocol: Ip4Protocol): void {
    setForm((current) => ({
      ...current,
      protocol,
      // Follow the protocol only while the port is still the one offered.
      port:
        current.port.trim() === String(IP4_DEFAULT_PORT[current.protocol])
          ? String(IP4_DEFAULT_PORT[protocol])
          : current.port,
    }));
  }

  function setQueue(key: LinkFeedQueueKey, patch: Partial<Ip4QueueForm>): void {
    setForm((current) => ({
      ...current,
      queues: { ...current.queues, [key]: { ...current.queues[key], ...patch } },
    }));
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const read = readIp4Form(form);
    if (!read.ok) {
      const message = t(PROBLEM_MESSAGE_KEY[read.problem]);
      setError(
        read.queue === undefined ? message : `${message} ${t(IP4_QUEUE_LABEL_KEY[read.queue])}`,
      );
      return;
    }
    if (password !== '' && password.length < IP4_PASSWORD_MIN_LENGTH) {
      setError(t('settings.integrations.ip4.form.passwordTooShort'));
      return;
    }
    const { value } = read;
    try {
      if (creating) {
        await create.mutateAsync({
          locationId,
          name: value.name,
          enabled,
          connection: {
            ...value.connection,
            credentials: { username: value.username, password },
          },
          feed: value.feed,
        });
      } else {
        const change = ip4Change(source, { ...value, enabled }, password);
        // Nothing changed: there is nothing to send, and nothing to audit.
        if (Object.keys(change).some((key) => key !== 'expectedUpdatedAt')) {
          await update.mutateAsync({ sourceId: source.id, change });
        }
      }
      onClose();
    } catch (cause) {
      setError(serverMessage(cause, t('settings.integrations.ip4.form.saveError')));
    }
  }

  const passwordRequired = creating || redirectsStoredLogin(source, form);
  const required = [form.name, form.host, form.port, form.username, form.shopId];
  const canSubmit =
    required.every((value) => value.trim() !== '') &&
    (!creating || locationId !== '') &&
    (!passwordRequired || password !== '');

  const startChanges = startNumberChanges(source, form.queues);
  const startNumberText = (value: number | null): string =>
    value === null ? t('settings.integrations.ip4.form.startNumberNone') : String(value);

  const freeLocations = (locations.data?.items ?? []).filter(
    (location) => !takenLocationIds.includes(location.id),
  );

  const checkbox = (
    id: string,
    label: string,
    checked: boolean,
    onChange: (value: boolean) => void,
  ) => (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <AlertDialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <form onSubmit={submit} className="space-y-5">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {creating
                ? t('settings.integrations.ip4.form.createTitle')
                : t('settings.integrations.ip4.form.editTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.ip4.form.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            {creating && (
              <div className="space-y-1.5">
                <Label htmlFor="ip4-location">{t('settings.integrations.ip4.form.location')}</Label>
                <Select
                  id="ip4-location"
                  value={locationId}
                  aria-describedby="ip4-location-hint"
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  <option value="">
                    {t('settings.integrations.ip4.form.locationPlaceholder')}
                  </option>
                  {freeLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </Select>
                <p id="ip4-location-hint" className="text-xs text-muted-foreground">
                  {t('settings.integrations.ip4.form.locationHint')}
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="ip4-name">{t('settings.integrations.ip4.form.name')}</Label>
              <Input
                id="ip4-name"
                value={form.name}
                maxLength={100}
                onChange={(event) => set('name', event.target.value)}
              />
            </div>
          </div>

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="mb-2 text-sm font-semibold">
              {t('settings.integrations.ip4.form.connection')}
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-protocol">{t('settings.integrations.ip4.form.protocol')}</Label>
              <Select
                id="ip4-protocol"
                value={form.protocol}
                onChange={(event) => setProtocol(event.target.value === 'amqp' ? 'amqp' : 'amqps')}
              >
                <option value="amqps">{t('settings.integrations.ip4.form.protocol.amqps')}</option>
                <option value="amqp">{t('settings.integrations.ip4.form.protocol.amqp')}</option>
              </Select>
              {form.protocol === 'amqp' && (
                <p role="note" className="text-xs text-destructive">
                  {t('settings.integrations.ip4.form.protocolPlainWarning')}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-host">{t('settings.integrations.ip4.form.host')}</Label>
              <Input
                id="ip4-host"
                value={form.host}
                maxLength={255}
                autoComplete="off"
                aria-describedby="ip4-host-hint"
                onChange={(event) => set('host', event.target.value)}
              />
              <p id="ip4-host-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.hostHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-port">{t('settings.integrations.ip4.form.port')}</Label>
              <Input
                id="ip4-port"
                value={form.port}
                maxLength={5}
                inputMode="numeric"
                onChange={(event) => set('port', event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-vhost">{t('settings.integrations.ip4.form.vhost')}</Label>
              <Input
                id="ip4-vhost"
                value={form.vhost}
                maxLength={255}
                autoComplete="off"
                aria-describedby="ip4-vhost-hint"
                onChange={(event) => set('vhost', event.target.value)}
              />
              <p id="ip4-vhost-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.vhostHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-username">{t('settings.integrations.ip4.form.username')}</Label>
              <Input
                id="ip4-username"
                value={form.username}
                maxLength={128}
                autoComplete="off"
                onChange={(event) => set('username', event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-password">{t('settings.integrations.ip4.form.password')}</Label>
              <Input
                id="ip4-password"
                type="password"
                autoComplete="new-password"
                value={password}
                aria-required={passwordRequired}
                aria-describedby={
                  !creating && passwordRequired
                    ? 'ip4-password-hint ip4-password-required'
                    : 'ip4-password-hint'
                }
                onChange={(event) => setPassword(event.target.value)}
              />
              <p id="ip4-password-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.passwordHint')}
                {!creating && !passwordRequired && (
                  <>
                    {' '}
                    {t('settings.integrations.ip4.form.passwordSet')}{' '}
                    {t('settings.integrations.ip4.form.passwordKeepHint')}
                  </>
                )}
              </p>
              {!creating && passwordRequired && (
                <p id="ip4-password-required" role="note" className="text-xs text-destructive">
                  {t('settings.integrations.ip4.form.passwordRequiredForBroker')}
                </p>
              )}
            </div>
          </fieldset>

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="mb-2 text-sm font-semibold">
              {t('settings.integrations.ip4.form.documents')}
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-shop-id">{t('settings.integrations.ip4.form.shopId')}</Label>
              <Input
                id="ip4-shop-id"
                value={form.shopId}
                maxLength={9}
                inputMode="numeric"
                aria-describedby="ip4-shop-id-hint"
                onChange={(event) => set('shopId', event.target.value)}
              />
              <p id="ip4-shop-id-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.shopIdHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-tax-number">
                {t('settings.integrations.ip4.form.taxNumber')}
              </Label>
              <Input
                id="ip4-tax-number"
                value={form.taxNumber}
                maxLength={32}
                aria-describedby="ip4-tax-number-hint"
                onChange={(event) => set('taxNumber', event.target.value)}
              />
              <p id="ip4-tax-number-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.taxNumberHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-header-lines">
                {t('settings.integrations.ip4.form.headerLines')}
              </Label>
              <Textarea
                id="ip4-header-lines"
                className="font-mono"
                rows={6}
                value={form.headerLines}
                aria-describedby="ip4-header-lines-hint"
                onChange={(event) => set('headerLines', event.target.value)}
              />
              <p id="ip4-header-lines-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.headerLinesHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ip4-footer-lines">
                {t('settings.integrations.ip4.form.footerLines')}
              </Label>
              <Textarea
                id="ip4-footer-lines"
                className="font-mono"
                rows={6}
                value={form.footerLines}
                aria-describedby="ip4-footer-lines-hint"
                onChange={(event) => set('footerLines', event.target.value)}
              />
              <p id="ip4-footer-lines-hint" className="text-xs text-muted-foreground">
                {t('settings.integrations.ip4.form.footerLinesHint')}
              </p>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="mb-2 text-sm font-semibold">
              {t('settings.integrations.ip4.form.queues')}
            </legend>
            <div
              role="note"
              className="space-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs"
            >
              <p>{t('settings.integrations.ip4.form.pauseNote')}</p>
              <p>{t('settings.integrations.ip4.form.startNumberNote')}</p>
            </div>
            <Table>
              <THead>
                <TR>
                  <TH scope="col">{t('settings.integrations.ip4.form.queue')}</TH>
                  <TH scope="col">{t('settings.integrations.ip4.form.queueEnabled')}</TH>
                  <TH scope="col">{t('settings.integrations.ip4.form.exchange')}</TH>
                  <TH scope="col">{t('settings.integrations.ip4.form.routingKey')}</TH>
                  <TH scope="col">{t('settings.integrations.ip4.form.startFromNumber')}</TH>
                </TR>
              </THead>
              <TBody>
                {LINK_FEED_QUEUE_KEYS.map((key) => {
                  const row = form.queues[key];
                  const name = t(IP4_QUEUE_LABEL_KEY[key]);
                  return (
                    <TR key={key}>
                      <TH
                        scope="row"
                        className="text-sm font-medium normal-case tracking-normal text-foreground"
                      >
                        {name}
                      </TH>
                      <TD>
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={row.enabled}
                          aria-label={`${name}: ${t('settings.integrations.ip4.form.queueEnabled')}`}
                          onChange={(event) => setQueue(key, { enabled: event.target.checked })}
                        />
                      </TD>
                      <TD>
                        <Input
                          className="min-w-[11rem] font-mono"
                          value={row.exchange}
                          maxLength={255}
                          autoComplete="off"
                          aria-label={`${name}: ${t('settings.integrations.ip4.form.exchange')}`}
                          onChange={(event) => setQueue(key, { exchange: event.target.value })}
                        />
                      </TD>
                      <TD>
                        <Input
                          className="min-w-[9rem] font-mono"
                          value={row.routingKey}
                          maxLength={255}
                          autoComplete="off"
                          aria-label={`${name}: ${t('settings.integrations.ip4.form.routingKey')}`}
                          onChange={(event) => setQueue(key, { routingKey: event.target.value })}
                        />
                      </TD>
                      <TD>
                        <Input
                          className="min-w-[7rem]"
                          value={row.startFromNumber}
                          maxLength={16}
                          inputMode="numeric"
                          aria-label={`${name}: ${t('settings.integrations.ip4.form.startFromNumber')}`}
                          onChange={(event) =>
                            setQueue(key, { startFromNumber: event.target.value })
                          }
                        />
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
            {startChanges.length > 0 && (
              <div
                role="note"
                aria-label={t('settings.integrations.ip4.form.startNumberChanged')}
                className="space-y-1 rounded-md border border-destructive/50 p-3 text-xs text-destructive"
              >
                <p className="font-medium">
                  {t('settings.integrations.ip4.form.startNumberChanged')}
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  {startChanges.map((change) => (
                    <li key={change.queue}>
                      {t(IP4_QUEUE_LABEL_KEY[change.queue])}: {startNumberText(change.from)} →{' '}
                      {startNumberText(change.to)} — {t(START_NUMBER_EFFECT_KEY[change.effect])}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.ip4.form.topologyHint')}
            </p>
          </fieldset>

          <div className="space-y-1">
            {checkbox(
              'ip4-enabled',
              t('settings.integrations.ip4.form.enabled'),
              enabled,
              setEnabled,
            )}
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.ip4.form.enabledHint')}
            </p>
          </div>

          {error !== null && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel type="button" onClick={onClose}>
              {t('settings.integrations.cancel')}
            </AlertDialogCancel>
            <Button type="submit" disabled={pending || !canSubmit}>
              {creating
                ? t('settings.integrations.ip4.form.createSubmit')
                : t('settings.integrations.ip4.form.editSubmit')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
